import { useQuery } from '@tanstack/react-query';

import { changelogSeenStore } from './changelog-seen.store';
import {
  CHANGELOG_FEED_URL,
  type ChangelogEntry,
  parseChangelogEntryPage,
  parseChangelogFeed,
} from './changelog.feed';

const ONE_HOUR_MS = 60 * 60 * 1000;
const RECENT_WINDOW_MS = 7 * 24 * ONE_HOUR_MS;

export const changelogKeys = {
  all: ['changelog'] as const,
  feed: () => [...changelogKeys.all, 'feed'] as const,
  page: (entryId: string) => [...changelogKeys.all, 'page', entryId] as const,
};

const fetchText = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} responded ${response.status}`);
  return response.text();
};

export const useChangelogFeedQuery = () =>
  useQuery({
    queryKey: changelogKeys.feed(),
    queryFn: async () =>
      parseChangelogFeed(await fetchText(CHANGELOG_FEED_URL)),
    staleTime: ONE_HOUR_MS,
    retry: 1,
  });

export const useChangelogEntryPageQuery = (entry: ChangelogEntry) =>
  useQuery({
    queryKey: changelogKeys.page(entry.id),
    queryFn: async () =>
      parseChangelogEntryPage(await fetchText(entry.url), entry.url),
    staleTime: Infinity,
    retry: 1,
  });

export const useUnseenChangelogEntry = (): ChangelogEntry | undefined => {
  const { data: entries } = useChangelogFeedQuery();
  const latest = entries?.[0];
  const isSeen = latest?.id === changelogSeenStore.lastSeenId;
  return latest && !isSeen ? latest : undefined;
};

export const useRecentUnseenChangelogEntry = (): ChangelogEntry | undefined => {
  const unseen = useUnseenChangelogEntry();
  const isRecent =
    !!unseen && Date.now() - unseen.publishedAt < RECENT_WINDOW_MS;
  return isRecent ? unseen : undefined;
};
